import { Controller } from "@hotwired/stimulus"
import { WEB_EVENTS } from "../lib/web_events"
import { WEB_DEFAULT_SETTINGS, WEB_SETTING_KEYS, readWebSetting } from "../lib/web_settings"
import { WEB_TRIP_POLICY } from "../lib/trip_policy"
import { CLIENT_POINT_BATCH_SIZE } from "../lib/trip_upload_policy"
import { tripRemoteGone, tripStateTerminal, responseErrorCode } from "../lib/tracker_remote_policy"
import {
  TRACKER_DISCARD_KEY,
  TRACKER_FINISH_KEY,
  TRACKER_LEASE_KEY,
  TRACKER_PAUSE_KEY,
  TRACKER_STOP_KEY,
  TRACKER_TRIP_KEY,
  buildTrackerLease,
  memoryOnlyMirrorEntries,
  newTrackerTabId,
  parsePendingStop,
  parseTrackerLease,
  safeStorageGet,
  safeStorageRemove,
  safeStorageSet,
  trackerLeaseAction,
  trackerLeaseFresh,
  trackerLeaseShouldYield,
  trackerPausedForTrip,
  trackerPendingCount,
  trackerRecordingActive,
  trackerStoredStateOwner,
  trimUnsentMirror
} from "../lib/tracker_storage_policy"
import { sanitizeSpeedMps, webTrackerPointAction } from "../lib/web_location_policy"
import { idbAdd, idbBatch, idbClear, idbCount, idbDelete, openPointDB } from "../lib/tracker_point_db"
import { apiFetch } from "../lib/api"
import { WEB_TRACKER_POLICY } from "../lib/web_policy"
import { WEB_PRESENCE_POLICY } from "../lib/presence_policy"
import { logEvent } from "../lib/client_logger"

// GPS trip recorder (web). Buffers points in IndexedDB and uploads them in ~60s
// batches, clearing local copies only after the server 200s — so losing signal
// or closing the tab never loses recorded points (they flush on next load).
//
// Browser limitation: a backgrounded/suspended tab stops delivering geolocation,
// so true background tracking isn't possible on web — the native app handles
// that. Here we hold a screen Wake Lock while recording and re-flush on resume.
const TRIP_KEY = TRACKER_TRIP_KEY
const FINISH_KEY = TRACKER_FINISH_KEY
const DISCARD_KEY = TRACKER_DISCARD_KEY
const OWNER_KEY = "gr8tracks:tripOwnerUserId"
const PAGEHIDE_POINT_BATCH_SIZE = WEB_TRACKER_POLICY.pagehidePointBatchSize
const MEMORY_POINT_BUFFER_SIZE = Math.max(PAGEHIDE_POINT_BATCH_SIZE, CLIENT_POINT_BATCH_SIZE)
// GPS watch gives up on a fix after this long; the error surfaces in the status line.
const GPS_WATCH_TIMEOUT_MS = WEB_TRACKER_POLICY.gpsWatchTimeoutMs
// Lease writes are throttled so per-fix updates don't spam storage events at other tabs.
const LEASE_REFRESH_THROTTLE_MS = WEB_TRACKER_POLICY.leaseRefreshThrottleMs

export default class extends Controller {
  static targets = ["startBtn", "stopBtn", "pauseBtn", "status", "saveModal", "nameInput",
                    "trackModal", "friendList", "nearbyOnly", "startTripBtn"]
  static values = { tripsUrl: String, friendsUrl: String, userId: Number, nearbyJoinRadiusMi: Number }

  async connect() {
    this.connected = true
    this.tabId = newTrackerTabId()
    this.unsentPointMirror = []
    this.storageDegraded = false
    this.mirrorOverflow = false
    try {
      this.db = await openPointDB()
    } catch (error) {
      this.db = null
      this.storageDegraded = true
      logEvent("tracker.storage_degraded", error)
    }
    if (!this.connected) return
    this.settingsHandler = e => {
      if (e.detail?.key === WEB_SETTING_KEYS.locationIntervalSec && this.tripId && !this.paused) this.#startFlusher()
    }
    window.addEventListener(WEB_EVENTS.settings, this.settingsHandler)
    // The map controller boots async (script + tile load), so it can miss the
    // resume-tracking event dispatched below. It asks once ready; re-announce.
    this.queryHandler = () => {
      if (this.tripId && (this.watchId != null || this.paused)) {
        window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: true, tripId: this.tripId } }))
      }
    }
    window.addEventListener(WEB_EVENTS.queryTracking, this.queryHandler)
    // The presence poller can learn the trip ended remotely before our own
    // points flush does (e.g. while paused) — it raises this event.
    this.tripEndedHandler = () => { if (this.tripId) this.#tripEndedRemotely() }
    window.addEventListener(WEB_EVENTS.tripEnded, this.tripEndedHandler)
    // Multi-tab coordination: a finish/discard in another tab stops our watch;
    // a priority lease write makes us yield recording.
    this.storageHandler = (e) => this.#onStorageEvent(e)
    window.addEventListener("storage", this.storageHandler)
    await this.#clearForeignStoredState()
    if (!this.connected) return
    this.tripId = safeStorageGet(TRIP_KEY)
    const pendingDiscard = this.#pendingDiscard()
    const pending = this.#pendingFinish()
    const pendingStop = this.#storedPendingStop()
    if (pendingDiscard) {
      this.tripId = String(pendingDiscard.id)
      this.#setInactiveButtons()
      await this.#retryPendingDiscard()
    } else if (pending) {
      this.tripId = String(pending.id)
      this.#setInactiveButtons()
      await this.#retryPendingFinish()
    } else if (pendingStop) {
      // Stop was tapped (Save/Discard on screen) when the tab closed — never
      // auto-resume recording; restore the finish UI for an explicit choice.
      this.tripId = String(pendingStop.id)
      this.pendingStop = pendingStop
      this.#setInactiveButtons()
      this.#showSaveModal(pendingStop.endedAt)
    } else if (this.tripId) {
      if (trackerLeaseAction({ raw: safeStorageGet(TRACKER_LEASE_KEY), tabId: this.tabId }) === "foreign") {
        // Another live tab owns this trip's recording — stay out of its way.
        this.#setInactiveButtons()
        this.statusTarget.textContent = "Recording in another tab — tracking stays off here."
      } else if (trackerPausedForTrip(safeStorageGet(TRACKER_PAUSE_KEY), this.tripId)) {
        // Persisted pause: restore the paused UI without restarting the watch.
        this.paused = true
        this.#setRecordingButtons()
        this.statusTarget.textContent = "Trip paused."
      } else {
        this.#beginRecording()
        this.statusTarget.textContent = "Resumed recording…"
      }
    } else {
      this.#setInactiveButtons({ showStart: true })
      await idbClear(this.db) // no trip owns leftover outbox rows — sweep orphans
      if (this.storageDegraded) this.statusTarget.textContent = "Tracker storage unavailable — keep this tab open while recording."
    }
  }

  disconnect() {
    this.connected = false
    if (this.settingsHandler) window.removeEventListener(WEB_EVENTS.settings, this.settingsHandler)
    if (this.queryHandler) window.removeEventListener(WEB_EVENTS.queryTracking, this.queryHandler)
    if (this.tripEndedHandler) window.removeEventListener(WEB_EVENTS.tripEnded, this.tripEndedHandler)
    if (this.storageHandler) window.removeEventListener("storage", this.storageHandler)
    this.#teardown()
  }

  // --- "Start tracking" sheet: track solo or invite friends (like the app) ---

  async openStart() {
    if (this.tripId) return // already recording
    if (!this.hasTrackModalTarget) { this.start(); return }
    this.invitees = new Set()
    if (this.hasNearbyOnlyTarget) this.nearbyOnlyTarget.checked = false
    this.#renderStartTripBtn()
    this.trackModalTarget.classList.remove("hidden")
    this.trackModalTarget.classList.add("flex")
    await this.#loadFriends()
  }

  cancelStart() {
    if (!this.hasTrackModalTarget) return
    this.trackModalTarget.classList.add("hidden")
    this.trackModalTarget.classList.remove("flex")
  }

  async confirmStart() {
    const inviteeIds = [...(this.invitees || [])]
    const nearbyOnly = this.hasNearbyOnlyTarget && this.nearbyOnlyTarget.checked
    this.cancelStart()
    await this.start()
    if (!this.tripId) return // start failed; status already shows the error
    if (!inviteeIds.length) return
    const joinRadiusMi = nearbyOnly ? this.#nearbyJoinRadiusMi() : null
    try {
      const res = await this.#post(`${this.tripsUrlValue}/${this.tripId}/invites`, {
        invitee_ids: inviteeIds, join_radius_mi: joinRadiusMi
      })
      if (!res.ok) throw new Error("invites failed")
      this.statusTarget.textContent = `Recording — invited ${inviteeIds.length} friend${inviteeIds.length > 1 ? "s" : ""}`
    } catch {
      this.statusTarget.textContent = "Recording — couldn't send invites"
    }
  }

  async #loadFriends() {
    if (!this.hasFriendListTarget) return
    this.friendListTarget.innerHTML = `<p class="py-2 text-sm text-slate-400">Loading friends…</p>`
    let friends = []
    try {
      const res = await this.#request(this.friendsUrlValue, { headers: { Accept: "application/json" } })
      if (res.ok) friends = await res.json()
    } catch (error) {
      logEvent("tracker.friends_load_failed", error)
    }
    if (!Array.isArray(friends) || !friends.length) {
      this.friendListTarget.innerHTML =
        `<a href="/friends" class="block py-2 text-sm font-semibold text-blue-600">＋ Add friends to track together</a>`
      return
    }
    this.friendListTarget.innerHTML = ""
    friends.forEach(f => this.friendListTarget.appendChild(this.#friendRow(f)))
  }

  #nearbyJoinRadiusMi() {
    const radius = this.hasNearbyJoinRadiusMiValue ? this.nearbyJoinRadiusMiValue : WEB_TRIP_POLICY.nearbyJoinRadiusMi
    return Number.isFinite(radius) && radius > 0 ? radius : WEB_TRIP_POLICY.nearbyJoinRadiusMi
  }

  #friendRow(f) {
    const label = document.createElement("label")
    label.className = "flex cursor-pointer items-center gap-3 border-b border-slate-100 py-2.5"
    const box = document.createElement("input")
    box.type = "checkbox"
    box.addEventListener("change", () => {
      if (box.checked) this.invitees.add(f.id)
      else this.invitees.delete(f.id)
      this.#renderStartTripBtn()
    })
    const dot = document.createElement("span")
    dot.className = `inline-block h-2.5 w-2.5 rounded-full ${f.on_trip ? "bg-orange-500" : f.online ? "bg-emerald-500" : "bg-slate-300"}`
    dot.title = f.on_trip ? "On a trip" : f.online ? "Online" : "Offline"
    const name = document.createElement("span")
    name.className = "font-semibold text-slate-700"
    name.textContent = f.display_name || f.email
    label.append(box, dot, name)
    return label
  }

  #renderStartTripBtn() {
    if (!this.hasStartTripBtnTarget) return
    const n = this.invitees?.size || 0
    this.startTripBtnTarget.textContent = n ? `Start with ${n} friend${n > 1 ? "s" : ""}` : "Start solo"
  }

  async start() {
    if (this.tripId || this.starting) return
    this.starting = true
    try {
      if (this.#pendingFinish()) {
        await this.#retryPendingFinish()
        if (this.#pendingFinish()) return
      }
      this.statusTarget.textContent = "Starting…"
      const res = await this.#post(this.tripsUrlValue, {})
      const trip = await res.json()
      if (!res.ok) throw new Error(trip.error || "Could not start trip")
      this.tripId = String(trip.id)
      if (!safeStorageSet(TRIP_KEY, this.tripId)) this.storageDegraded = true
      this.#saveOwner()
      this.paused = false
      this.#beginRecording()
    } catch (e) {
      this.statusTarget.textContent = e.message || String(e)
    } finally {
      this.starting = false
    }
  }

  async stop() {
    const id = this.tripId
    if (!id) return
    const endedAt = new Date().toISOString()
    this.#teardown()
    this.pendingStop = { id, endedAt, wasPaused: !!this.paused }
    this.#savePendingStop(this.pendingStop) // survive a reload without auto-resuming
    this.#setInactiveButtons()
    this.#showSaveModal(endedAt)
  }

  async confirmSave() {
    const pending = this.pendingStop
    if (!pending) return
    const fallbackName = defaultTripName(pending.endedAt)
    const name = (this.hasNameInputTarget ? this.nameInputTarget.value : fallbackName).trim() || fallbackName
    this.#hideSaveModal()
    safeStorageRemove(TRACKER_STOP_KEY)
    safeStorageRemove(TRACKER_PAUSE_KEY)
    this.#savePendingFinish(pending.id, pending.endedAt, name)
    this.#setInactiveButtons()
    const unsentPoints = (await this.#flush()) || 0 // send anything still buffered
    await this.#finishRemote(pending.id, pending.endedAt, name, { unsentPoints })
    this.pendingStop = null
  }

  async confirmDiscard() {
    const pending = this.pendingStop
    if (!pending) return
    this.#hideSaveModal()
    safeStorageRemove(TRACKER_STOP_KEY)
    safeStorageRemove(TRACKER_PAUSE_KEY)
    await this.#discardTrip(pending.id)
    this.pendingStop = null
  }

  cancelSave() {
    if (!this.pendingStop) return
    const wasPaused = this.pendingStop.wasPaused
    this.#hideSaveModal()
    this.pendingStop = null
    safeStorageRemove(TRACKER_STOP_KEY)
    if (wasPaused) {
      this.paused = true
      if (!safeStorageSet(TRACKER_PAUSE_KEY, String(this.tripId))) this.storageDegraded = true
      this.#setRecordingButtons()
      this.statusTarget.textContent = "Trip paused."
      return
    }
    this.#beginRecording({ resetCount: false, notify: false })
    this.statusTarget.textContent = "Recording resumed."
  }

  async pause() {
    if (!this.tripId) return
    if (this.paused) {
      this.paused = false
      this.#beginRecording({ resetCount: false, notify: false })
      return
    }
    this.paused = true
    if (!safeStorageSet(TRACKER_PAUSE_KEY, String(this.tripId))) this.storageDegraded = true
    this.#teardown()
    this.#setRecordingButtons()
    this.statusTarget.textContent = "Trip paused."
  }

  async #discardTrip(id) {
    this.#teardown()
    this.#savePendingDiscard(id)
    this.#setInactiveButtons()
    await idbClear(this.db)
    this.unsentPointMirror = []
    await this.#deleteRemote(id)
  }

  #beginRecording({ resetCount = true, notify = true } = {}) {
    if (trackerRecordingActive(this)) {
      this.paused = false
      safeStorageRemove(TRACKER_PAUSE_KEY)
      this.#setRecordingButtons()
      this.#startFlusher()
      if (notify) window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: true, tripId: this.tripId } }))
      this.#updateStatus()
      return
    }
    if (!this.#claimRecordingLease()) {
      // A live lease from another tab owns this trip — do not double-record.
      this.paused = false
      this.#setInactiveButtons()
      this.statusTarget.textContent = "Recording in another tab — tracking stays off here."
      return
    }
    if (resetCount) {
      this.count = 0
      this.lastAcceptedFix = null
    }
    this.paused = false
    safeStorageRemove(TRACKER_PAUSE_KEY)
    this.#setRecordingButtons()
    this.#acquireWakeLock()

    this.watchId = navigator.geolocation.watchPosition(
      (pos) => { if (this.connected) this.#record(pos) },
      (err) => { if (this.connected) this.statusTarget.textContent = `GPS: ${err.message}` },
      { enableHighAccuracy: true, maximumAge: 0, timeout: GPS_WATCH_TIMEOUT_MS }
    )
    this.#startFlusher()

    // Re-acquire the wake lock when returning to the tab; flush when leaving.
    this.visHandler = () => {
      if (document.visibilityState === "visible") this.#acquireWakeLock()
      else this.#lastChanceFlush()
    }
    document.addEventListener("visibilitychange", this.visHandler)
    this.hideHandler = () => this.#lastChanceFlush()
    window.addEventListener("pagehide", this.hideHandler)
    if (notify) window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: true, tripId: this.tripId } }))
    this.#updateStatus()
  }

  async #record(pos) {
    const c = pos.coords
    const speed = sanitizeSpeedMps(c.speed)
    const fix = { lat: c.latitude, lon: c.longitude, accuracy: c.accuracy, speed }
    const action = webTrackerPointAction(fix, this.lastAcceptedFix)
    if (action.kind === "drop") return
    if (action.kind === "stationary") {
      if (this.lastAcceptedFix) {
        this.#dispatchPosition({
          lat: this.lastAcceptedFix.lat,
          lon: this.lastAcceptedFix.lon,
          speed: 0,
          altitude: c.altitude,
          heading: c.heading
        })
      }
      return
    }

    const point = {
      lat: c.latitude, lon: c.longitude,
      elevation: c.altitude, accuracy: c.accuracy, speed,
      recorded_at: new Date(pos.timestamp).toISOString()
    }
    // Commit the accept decision BEFORE any await. Geolocation callbacks are not
    // serialized: two fixes a second apart both reach the filter above, and if
    // this ran after idbAdd they would both have been compared against the same
    // stale reference, then raced to write it — leaving lastAcceptedFix holding
    // whichever idbAdd finished last, which can be the OLDER fix. Every later
    // jitter/stationary decision is made against that corrupted reference.
    this.lastAcceptedFix = fix
    this.count = (this.count || 0) + 1

    const mirrored = this.#rememberUnsentPoint(point)
    try {
      mirrored.id = await idbAdd(this.db, point, this.tripId)
      mirrored.persisting = false
      // A pagehide flush may have uploaded this point from memory while the add
      // was still in flight (see #flushMirroredPoints). The durable row is now a
      // second copy of something the server already has, and the next ordinary
      // flush would post it again — so retire it here instead.
      if (mirrored.uploaded) {
        await idbDelete(this.db, [mirrored.id]).catch(() => {})
        this.#forgetUnsentPointIds([mirrored.id])
      }
    } catch {
      // The IDB write did not become durable; this entry is now the sole copy
      // and becomes eligible for the memory-only uploader.
      mirrored.persisting = false
      this.storageDegraded = true
    }
    this.#updateStatus()
    this.#refreshRecordingLease()
    // Feed the map's follow-camera + speed/elevation HUD.
    this.#dispatchPosition({ lat: c.latitude, lon: c.longitude, speed, altitude: c.altitude, heading: c.heading })
  }

  #dispatchPosition(detail) {
    window.dispatchEvent(new CustomEvent(WEB_EVENTS.position, {
      detail
    }))
  }

  async #flush(options = {}) {
    if (!this.tripId) return
    if (this.flushPromise) {
      this.flushAgain = true
      if (options.keepalive) this.flushKeepalive = true
      return this.flushPromise
    }
    this.flushAgain = false
    this.flushKeepalive = !!options.keepalive
    this.flushPromise = this.#flushLoop().finally(() => {
      this.flushPromise = null
      this.flushAgain = false
      this.flushKeepalive = false
    })
    return this.flushPromise
  }

  async #flushLoop() {
    let remaining = 0
    do {
      const keepalive = !!this.flushKeepalive
      this.flushAgain = false
      this.flushKeepalive = false
      remaining = await this.#flushOnce({ keepalive })
    } while (this.flushAgain && this.tripId)
    return remaining
  }

  async #flushOnce({ keepalive = false } = {}) {
    const memoryOutcome = await this.#flushMemoryOnlyPoints({ keepalive })
    let terminal = memoryOutcome === "terminal"
    // Upload one IndexedDB cursor batch at a time so a long offline session never
    // materializes the whole outbox in browser memory.
    while (!terminal) {
      let chunk = []
      try {
        chunk = await idbBatch(this.db, CLIENT_POINT_BATCH_SIZE, this.tripId)
      } catch { break }
      if (!chunk.length) break
      try {
        const res = await this.#post(`${this.tripsUrlValue}/${this.tripId}/points`, { points: chunk.map((r) => r.point) }, { keepalive })
        const outcome = await this.#pointsUploadOutcome(res)
        if (outcome !== "ok") { terminal = outcome === "terminal"; break }
        await idbDelete(this.db, chunk.map((r) => r.id))
        this.#forgetUnsentPointIds(chunk.map((r) => r.id))
      } catch { break /* keep buffered; retry on the next flush */ }
    }
    // A terminal trip-state rejection means the trip ended elsewhere: stop
    // recording and hand the buffered points to the Save/Discard UI. Skip the
    // normal status refresh so it can't overwrite that message.
    if (terminal) await this.#tripEndedRemotely()
    else this.#updateStatus()
    return this.#pendingPointCount()
  }

  // "ok" uploaded · "retry" transient failure, keep buffered · "terminal" the
  // trip is finished/superseded elsewhere — stop, don't retry.
  async #pointsUploadOutcome(res) {
    if (res.ok) return "ok"
    return tripStateTerminal(await responseErrorCode(res)) ? "terminal" : "retry"
  }

  async #pendingPointCount() {
    let dbPending = 0
    try { dbPending = await idbCount(this.db) } catch { this.storageDegraded = true }
    return trackerPendingCount(dbPending, this.unsentPointMirror, this.tripId)
  }

  #startFlusher() {
    if (this.flusher) clearInterval(this.flusher)
    this.flusher = setInterval(() => {
      this.#refreshRecordingLease()
      this.#flush().catch(() => {})
    }, locationFlushMs())
  }

  async #retryPendingFinish() {
    const pending = this.#pendingFinish()
    if (!pending) return true
    this.tripId = String(pending.id)
    this.statusTarget.textContent = "Finishing saved trip…"
    const unsentPoints = (await this.#flush()) || 0
    return this.#finishRemote(pending.id, pending.ended_at, pending.name, { unsentPoints })
  }

  async #finishRemote(id, endedAt, name = null, { unsentPoints = 0 } = {}) {
    try {
      const res = await this.#post(`${this.tripsUrlValue}/${id}/finish`, { ended_at: endedAt, ...(name ? { name } : {}) })
      if (tripRemoteGone(res.status)) {
        await idbClear(this.db)
        this.#clearTripState()
        this.#setInactiveButtons({ showStart: true })
        window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: false } }))
        this.statusTarget.textContent = "Trip already removed."
        return true
      }
      if (!res.ok) throw new Error("finish failed")
      // Terminal success: the outbox belongs to this finished trip — always
      // clear it so leftover rows can never leak into the NEXT trip's batches.
      await idbClear(this.db)
      this.#clearTripState()
      this.#setInactiveButtons({ showStart: true })
      window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: false } }))
      const unsent = Number(unsentPoints) || 0
      this.statusTarget.textContent = unsent > 0
        ? `Trip saved — ${unsent} point${unsent === 1 ? "" : "s"} couldn't upload.`
        : "Trip saved."
      return true
    } catch {
      this.#setInactiveButtons()
      this.statusTarget.textContent = this.storageDegraded
        ? "Trip finish pending — keep this tab open to retry."
        : "Trip finish pending — will retry when connection returns."
      return false
    }
  }

  async #retryPendingDiscard() {
    const pending = this.#pendingDiscard()
    if (!pending) return true
    this.statusTarget.textContent = "Discarding trip…"
    await idbClear(this.db)
    this.unsentPointMirror = []
    return this.#deleteRemote(pending.id)
  }

  async #deleteRemote(id) {
    try {
      const res = await this.#request(`${this.tripsUrlValue}/${id}`, { method: "DELETE" })
      if (tripRemoteGone(res.status)) {
        this.#clearTripState()
        safeStorageRemove(DISCARD_KEY)
        this.#setInactiveButtons({ showStart: true })
        window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: false } }))
        this.statusTarget.textContent = "Trip discarded."
        return true
      }
      if (!res.ok) throw new Error("delete failed")
      this.#clearTripState()
      safeStorageRemove(DISCARD_KEY)
      this.#setInactiveButtons({ showStart: true })
      window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: false } }))
      this.statusTarget.textContent = "Trip discarded."
      return true
    } catch {
      this.#setInactiveButtons()
      this.statusTarget.textContent = this.storageDegraded
        ? "Trip discard pending — keep this tab open to retry."
        : "Trip discard pending — will retry when connection returns."
      return false
    }
  }

  // The trip was finished/superseded on another device (or otherwise stopped
  // recording server-side): stop being a zombie. Keep the buffered points —
  // they are the user's data — and route them to the Save/Discard pending UI.
  async #tripEndedRemotely() {
    const id = this.tripId
    if (!id || this.endedRemoteId === String(id)) return
    this.endedRemoteId = String(id)
    this.#teardown()
    this.paused = false
    safeStorageRemove(TRACKER_PAUSE_KEY)
    this.pendingStop = { id: String(id), endedAt: new Date().toISOString(), wasPaused: false }
    this.#savePendingStop(this.pendingStop) // a reload restores this same choice
    this.#setInactiveButtons()
    this.#showSaveModal(this.pendingStop.endedAt)
    window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: false } }))
    this.statusTarget.textContent = "This trip was ended from another device — Save to keep it or Discard it."
  }

  // Cross-tab coordination (storage events only fire in OTHER tabs, so a change
  // seen here was made elsewhere): a cleared trip key means the trip is over;
  // a finish/discard key means it's about to be — stop our watch promptly.
  #onStorageEvent(e) {
    if (!this.connected || !e) return
    if (e.key === TRIP_KEY && !e.newValue) {
      this.#tripClearedElsewhere()
      return
    }
    if ((e.key === FINISH_KEY || e.key === DISCARD_KEY) && e.newValue && this.tripId && trackerRecordingActive(this)) {
      this.#teardown()
      this.paused = false
      window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: false } }))
      this.statusTarget.textContent = e.key === FINISH_KEY
        ? "Trip is being finished in another tab."
        : "Trip is being discarded in another tab."
      return
    }
    if (e.key === TRACKER_LEASE_KEY && e.newValue && this.tripId && trackerRecordingActive(this) &&
        trackerLeaseShouldYield({ raw: e.newValue, tabId: this.tabId, claimedAt: this.leaseClaimedAt })) {
      this.#teardown() // also releases our lease claim (theirs is stored now)
      this.paused = false
      this.#setInactiveButtons()
      window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: false } }))
      this.statusTarget.textContent = "Recording in another tab — tracking stays off here."
    }
  }

  #tripClearedElsewhere() {
    if (!this.tripId) return
    const wasLive = trackerRecordingActive(this) || this.paused
    // In degraded-storage mode the mirror may be the only copy. Start the same
    // keepalive request used by pagehide while tripId and the points still exist;
    // storage events cannot await, but fetch captures the body synchronously.
    this.#flushMirroredPoints()
    this.#teardown()
    this.tripId = null
    this.paused = false
    this.pendingStop = null
    this.endedRemoteId = null
    this.unsentPointMirror = []
    this.mirrorOverflow = false
    this.#hideSaveModal()
    this.#setInactiveButtons({ showStart: true })
    if (wasLive) window.dispatchEvent(new CustomEvent(WEB_EVENTS.tracking, { detail: { tracking: false } }))
    this.statusTarget.textContent = "Trip ended in another tab."
  }

  // --- multi-tab recording lease (one recorder per trip) ---------------------

  #claimRecordingLease() {
    const action = trackerLeaseAction({ raw: safeStorageGet(TRACKER_LEASE_KEY), tabId: this.tabId })
    if (action === "foreign") return false
    if (action !== "self" || this.leaseClaimedAt == null) this.leaseClaimedAt = Date.now()
    this.#writeRecordingLease()
    return true
  }

  #writeRecordingLease(at = Date.now()) {
    this.leaseWrittenAt = at
    const lease = buildTrackerLease({ tabId: this.tabId, tripId: this.tripId, claimedAt: this.leaseClaimedAt ?? at, at })
    if (!safeStorageSet(TRACKER_LEASE_KEY, JSON.stringify(lease))) this.storageDegraded = true
  }

  // Fixes + flusher ticks keep the lease alive; throttled so we don't spam
  // storage events in other tabs on every GPS fix.
  #refreshRecordingLease() {
    if (!this.tripId || this.leaseClaimedAt == null) return
    const now = Date.now()
    if (now - (this.leaseWrittenAt || 0) < LEASE_REFRESH_THROTTLE_MS) return
    const stored = parseTrackerLease(safeStorageGet(TRACKER_LEASE_KEY))
    if (stored && stored.tab !== this.tabId && trackerLeaseFresh(stored, now)) return // foreign tab holds it; its storage event will yield us
    this.#writeRecordingLease(now)
  }

  #releaseRecordingLease() {
    if (this.leaseClaimedAt == null) return
    this.leaseClaimedAt = null
    const stored = parseTrackerLease(safeStorageGet(TRACKER_LEASE_KEY))
    if (stored?.tab === this.tabId) safeStorageRemove(TRACKER_LEASE_KEY)
  }

  async #updateStatus() {
    if (!this.connected) return
    if (this.paused) {
      this.statusTarget.textContent = "Trip paused."
      return
    }
    const pending = await this.#pendingPointCount()
    if (!this.connected || this.paused) return
    // A stop / remote end / other-tab takeover during the await must not be
    // overwritten by a stale "Recording" line — paint it only while a
    // recorder is actually running in this tab.
    if (!trackerRecordingActive(this)) return
    const fallback = this.storageDegraded ? " · storage fallback" : ""
    const overflow = this.mirrorOverflow ? " · buffer full — points being lost" : ""
    this.statusTarget.textContent = `Recording — ${this.count || 0} pts (${pending} unsent)${fallback}${overflow}`
  }

  #lastChanceFlush() {
    if (!this.#flushMirroredPoints()) this.#flush({ keepalive: true }).catch(() => {})
  }

  #rememberUnsentPoint(point) {
    // Exclude this point from the normal memory-only flusher until idbAdd
    // settles. Otherwise a flusher between add() and its success callback can
    // POST it once from memory and again later from the durable IDB row.
    const entry = { id: null, persisting: true, point, trip: this.tripId != null ? String(this.tripId) : null }
    this.unsentPointMirror ||= []
    this.unsentPointMirror.push(entry)
    // IndexedDB-backed entries drop first; memory-only entries are the sole
    // copy of a point, so trimming those means real data loss — surface it.
    const trimmed = trimUnsentMirror(this.unsentPointMirror, MEMORY_POINT_BUFFER_SIZE)
    this.unsentPointMirror = trimmed.entries
    if (trimmed.droppedUnsent > 0) this.mirrorOverflow = true
    return entry
  }

  #forgetUnsentPointIds(ids) {
    if (!this.unsentPointMirror?.length) return
    const deleted = new Set(ids)
    this.unsentPointMirror = this.unsentPointMirror.filter((entry) => entry.id == null || !deleted.has(entry.id))
  }

  #flushMirroredPoints() {
    if (!this.tripId || !this.unsentPointMirror?.length) return false
    if (this.pagehideFlushInFlight) return true
    const entries = this.unsentPointMirror
      .filter(entry => !entry.pagehideSubmitted && entry.point && (entry.trip == null || String(entry.trip) === String(this.tripId)))
      .slice(-PAGEHIDE_POINT_BATCH_SIZE)
    if (!entries.length) return false
    entries.forEach(entry => { entry.pagehideSubmitted = true })
    const points = entries.map(entry => entry.point)
    const submitted = new Set(entries)
    const ids = entries.map(entry => entry.id).filter(id => id != null)
    try {
      const request = this.#post(`${this.tripsUrlValue}/${this.tripId}/points`, { points }, { keepalive: true })
      this.pagehideFlushInFlight = request
      request.then(async res => {
        const outcome = await this.#pointsUploadOutcome(res)
        if (outcome === "terminal") {
          await this.#tripEndedRemotely()
          return
        }
        if (outcome !== "ok") return
        // Deliberately NOT filtered on `persisting` the way the ordinary
        // memory-only flusher is. This is the last chance to save a point whose
        // idbAdd has not settled — skipping it to avoid a duplicate would trade a
        // harmless deduped write (Rails is idempotent on trip_id + recorded_at)
        // for actually losing the point if the page dies first. Instead the entry
        // is marked so #record retires the durable row once the add lands.
        submitted.forEach(entry => { entry.uploaded = true })
        if (ids.length) await idbDelete(this.db, ids)
        this.unsentPointMirror = this.unsentPointMirror.filter(entry => !submitted.has(entry))
      }).catch(() => {}).finally(() => {
        entries.forEach(entry => { entry.pagehideSubmitted = false })
        if (this.pagehideFlushInFlight === request) this.pagehideFlushInFlight = null
      })
      return true
    } catch {
      entries.forEach(entry => { entry.pagehideSubmitted = false })
      return false
    }
  }

  async #flushMemoryOnlyPoints({ keepalive = false } = {}) {
    while (this.tripId) {
      const entries = memoryOnlyMirrorEntries(this.unsentPointMirror, this.tripId).slice(0, CLIENT_POINT_BATCH_SIZE)
      if (!entries.length) return "ok"
      try {
        const res = await this.#post(`${this.tripsUrlValue}/${this.tripId}/points`, { points: entries.map((entry) => entry.point) }, { keepalive })
        const outcome = await this.#pointsUploadOutcome(res)
        if (outcome !== "ok") return outcome
        const sent = new Set(entries)
        this.unsentPointMirror = this.unsentPointMirror.filter((entry) => !sent.has(entry))
      } catch {
        return "retry"
      }
    }
    return "ok"
  }

  async #acquireWakeLock() {
    try { if ("wakeLock" in navigator) this.wakeLock = await navigator.wakeLock.request("screen") } catch { /* ok */ }
  }

  #teardown() {
    if (this.watchId != null) navigator.geolocation.clearWatch(this.watchId)
    if (this.flusher) clearInterval(this.flusher)
    if (this.visHandler) document.removeEventListener("visibilitychange", this.visHandler)
    if (this.hideHandler) window.removeEventListener("pagehide", this.hideHandler)
    if (this.wakeLock) { this.wakeLock.release().catch(() => {}); this.wakeLock = null }
    this.watchId = this.flusher = null
    this.visHandler = this.hideHandler = null
    this.#releaseRecordingLease()
  }

  #setInactiveButtons({ showStart = false } = {}) {
    this.startBtnTarget?.classList.toggle("hidden", !showStart)
    this.stopBtnTarget?.classList.add("hidden")
    this.stopBtnTarget?.classList.remove("flex")
    if (this.hasPauseBtnTarget) {
      this.pauseBtnTarget.classList.add("hidden")
      this.pauseBtnTarget.classList.remove("flex")
    }
  }

  #setRecordingButtons() {
    this.startBtnTarget?.classList.add("hidden")
    this.stopBtnTarget?.classList.remove("hidden")
    this.stopBtnTarget?.classList.add("flex")
    if (this.hasPauseBtnTarget) {
      this.pauseBtnTarget.classList.remove("hidden")
      this.pauseBtnTarget.classList.add("flex")
      // Icon/label spans keep the button's styled markup intact (a flat
      // textContent write would flatten them), like the Stop button's spans.
      const icon = this.pauseBtnTarget.querySelector("[data-pause-icon]")
      if (icon) icon.textContent = this.paused ? "▶" : "Ⅱ"
      const label = this.pauseBtnTarget.querySelector("[data-pause-label]")
      if (label) label.textContent = this.paused ? "Resume" : "Pause"
    }
  }

  #showSaveModal(endedAt) {
    if (this.hasNameInputTarget) this.nameInputTarget.value = defaultTripName(endedAt)
    if (this.hasSaveModalTarget) {
      this.saveModalTarget.classList.remove("hidden")
      this.saveModalTarget.classList.add("flex")
    }
    if (this.hasNameInputTarget) this.nameInputTarget.focus()
    this.statusTarget.textContent = "Choose how to finish this trip."
  }

  #hideSaveModal() {
    if (!this.hasSaveModalTarget) return
    this.saveModalTarget.classList.add("hidden")
    this.saveModalTarget.classList.remove("flex")
  }

  #savePendingFinish(id, endedAt, name = null) {
    this.#saveOwner()
    if (!safeStorageSet(FINISH_KEY, JSON.stringify({ id: String(id), ended_at: endedAt, ...(name ? { name } : {}) }))) this.storageDegraded = true
  }

  #savePendingStop(pending) {
    this.#saveOwner()
    if (!safeStorageSet(TRACKER_STOP_KEY, JSON.stringify({ id: String(pending.id), ended_at: pending.endedAt, paused: !!pending.wasPaused }))) this.storageDegraded = true
  }

  #storedPendingStop() {
    return parsePendingStop(safeStorageGet(TRACKER_STOP_KEY))
  }

  #savePendingDiscard(id) {
    this.#saveOwner()
    if (!safeStorageSet(DISCARD_KEY, JSON.stringify({ id: String(id) }))) this.storageDegraded = true
  }

  #pendingFinish() {
    try {
      const pending = JSON.parse(safeStorageGet(FINISH_KEY) || "null")
      return pending?.id && pending?.ended_at ? pending : null
    } catch {
      return null
    }
  }

  #pendingDiscard() {
    try {
      const pending = JSON.parse(safeStorageGet(DISCARD_KEY) || "null")
      return pending?.id ? pending : null
    } catch {
      return null
    }
  }

  #clearTripState() {
    safeStorageRemove(FINISH_KEY)
    safeStorageRemove(DISCARD_KEY)
    safeStorageRemove(TRIP_KEY)
    safeStorageRemove(OWNER_KEY)
    safeStorageRemove(TRACKER_STOP_KEY)
    safeStorageRemove(TRACKER_PAUSE_KEY)
    this.#releaseRecordingLease()
    this.unsentPointMirror = []
    this.mirrorOverflow = false
    this.tripId = null
    this.paused = false
    this.pendingStop = null
    this.endedRemoteId = null
    this.lastAcceptedFix = null
    this.#hideSaveModal()
  }

  #hasStoredTripState() {
    return !!(safeStorageGet(TRIP_KEY) || safeStorageGet(FINISH_KEY) || safeStorageGet(DISCARD_KEY) ||
      safeStorageGet(TRACKER_STOP_KEY) || safeStorageGet(TRACKER_PAUSE_KEY))
  }

  #saveOwner() {
    if (this.hasUserIdValue && !safeStorageSet(OWNER_KEY, String(this.userIdValue))) this.storageDegraded = true
  }

  async #clearForeignStoredState() {
    const owner = trackerStoredStateOwner({
      hasStoredState: this.#hasStoredTripState(),
      storedOwnerId: safeStorageGet(OWNER_KEY),
      currentUserId: this.hasUserIdValue ? this.userIdValue : null
    })
    if (owner !== "foreign") return
    this.#clearTripState()
    this.unsentPointMirror = []
    await idbClear(this.db)
  }

  #post(url, body, options = {}) {
    return this.#request(url, {
      method: "POST",
      body: JSON.stringify(body),
      ...(options.keepalive ? { keepalive: true } : {})
    })
  }

  #request(url, options = {}) {
    return apiFetch(url, options)
  }
}

function defaultTripName(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "Trip"
  return `Trip ${d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}`
}

function locationFlushMs() {
  const sec = Number(readWebSetting(WEB_SETTING_KEYS.locationIntervalSec))
  const fallback = WEB_DEFAULT_SETTINGS.locationIntervalSec
  return Math.max(
    WEB_PRESENCE_POLICY.minIntervalSec,
    Math.min(WEB_PRESENCE_POLICY.maxIntervalSec, Number.isFinite(sec) ? sec : fallback)
  ) * 1000
}
